import { Box, Paper } from '@dhl-ui/components'
import { useListReturnHubs } from '@dhl/mdp-api'
import { toGroupedProducts, getSortedProductGroups, type Product, type ProductGroup } from '~/common/model/capabilities'
import { useCurrentUser } from '~/common/hooks'
import { Header, getCheckboxState, Row } from '~/common/components/accountnumbers'
import { convertToReturnHub } from '~/modules/returnhubsccc/model'

export type ProductDataTableColumn = 'selectAll' | 'selectProduct' | 'selectReturnProduct'

type Props = {
  products: Product[]
  selectedProducts: Product[]
  onChangeSelectedProducts: (selectedProducts: Product[]) => void
  columns: ProductDataTableColumn[]
  isCCCOrg: boolean
  disabled?: boolean
}

export const ProductDataTable = ({
  products,
  selectedProducts,
  onChangeSelectedProducts,
  columns,
  isCCCOrg,
  disabled = false,
}: Props) => {
  const { isCCC } = useCurrentUser()
  const groupedProducts = toGroupedProducts(products)
  const sortedProductGroups = getSortedProductGroups(groupedProducts, isCCC)
  const selectAllState = getCheckboxState(selectedProducts, products)
  const returnHubsEnabled = isCCC && isCCCOrg
  const { data: returnHubs = [] } = useListReturnHubs({
    query: { enabled: returnHubsEnabled, select: (items) => items.map(convertToReturnHub) },
  })

  const handleSelectAll = (deselectAll: boolean) => {
    onChangeSelectedProducts(deselectAll ? [] : products)
  }

  const handleChangeSelectedProducts = (productGroup: ProductGroup, selectedGroupProducts: Product[]) => {
    const otherSelectedProducts = selectedProducts.filter(({ group }) => group !== productGroup)
    onChangeSelectedProducts([...otherSelectedProducts, ...selectedGroupProducts])
  }

  return (
    <Paper isOutlined isRounded sx={{ pb: 2 }}>
      <Header selectAllState={selectAllState} onSelectAll={handleSelectAll} columns={columns} disabled={disabled} />
      {sortedProductGroups.map((productGroup, index) => {
        const products = groupedProducts[productGroup]!
        const isAlternateRow = index % 2 === 1
        return (
          <Row
            isCCCOrg={isCCCOrg}
            columns={columns}
            key={productGroup}
            isAlternateRow={isAlternateRow}
            productGroup={productGroup}
            products={products}
            disabled={disabled}
            returnHubs={returnHubs}
            selectedProducts={selectedProducts.filter(({ group }) => group === productGroup)}
            onChangeSelectedProducts={(selectedGroupProducts) =>
              handleChangeSelectedProducts(productGroup, selectedGroupProducts)
            }
          />
        )
      })}
    </Paper>
  )
}
